/**
 * Where the site menu goes when you switch sites (UX review round 8, Prototype
 * v14): the same screen and tab, at its list. Content → Forms stays Content →
 * Forms, Reach → Channels stays Channels. Anything naming one item — an open
 * article, a selected form, page, post, campaign or Write brief — is
 * dropped, because an id from one site means nothing on the other. From the
 * Sites screen (or nowhere in particular) it's Today.
 */

/** Screens that are their own list: kept as they are, minus any query. */
const SCREEN_PREFIXES = [
  '/today',
  '/content/settings',
  '/content/media/settings',
  '/content/pages/settings',
  '/content/forms/settings',
  '/site/settings',
  '/reach/calendar',
  '/campaigns',
  '/reach/automatic',
  '/reach/channels',
  '/prism-ai/research',
  '/prism-ai/write',
  '/collection',
  '/capture',
  '/media',
  '/settings',
];

/** Query values that pick a tab, not an item: kept. */
const TAB_PARAMS = new Set(['view', 'filter', 'tab', 'area']);

export function pathAfterSiteSwitch(pathname: string, search = ''): string {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/' || path === '/sites') return '/today';

  // /content/forms etc. are tabs (App.tsx redirects them to ?view=), not ids.
  const tab = /^\/content\/(pages|articles|forms|media|design)$/.exec(path);
  if (tab) return `/content?view=${tab[1]}`;
  // An open article (/content/<id>) goes back to the Articles list.
  if (/^\/content\/[^/]+$/.test(path) && !/^\/content\/settings$/.test(path)) {
    return '/content?view=articles';
  }
  // A collection item goes back to the collection.
  if (/^\/collection\/[^/]+$/.test(path)) return '/collection';

  if (path === '/content') {
    const params = new URLSearchParams(search);
    const kept = new URLSearchParams();
    // The tab survives (?view=forms); the form, page or search inside it doesn't.
    const view = params.get('view');
    if (view) kept.set('view', view);
    const q = kept.toString();
    return q ? `/content?${q}` : '/content';
  }

  if (SCREEN_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
    const params = new URLSearchParams(search);
    const kept = new URLSearchParams();
    params.forEach((v, k) => {
      if (TAB_PARAMS.has(k)) kept.set(k, v);
    });
    const q = kept.toString();
    return q ? `${path}?${q}` : path;
  }
  return '/today';
}
